"use client";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ModalProps } from "@/types/modal";

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title = "Fill the form here",
    children,
    size = "md",
    showCloseButton = true,
    className = "",
    overlayClassName = "",
    closeOnOverlayClick = true,
    closeOnEscape = true,
}) => {
    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (closeOnEscape && e.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, closeOnEscape, onClose]);

    // Handle overlay click
    const handleOverlayClick = (e: React.MouseEvent) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    const sizeClasses = {
        sm: "modal-sm",
        md: "modal-md",
        lg: "modal-lg",
        xl: "modal-xl",
    };

    const modalContent = (
        <div
            className={`modal-overlay ${overlayClassName}`}
            onClick={handleOverlayClick}
        >
            <div
                className={`modal-container ${sizeClasses[size]} ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="modal-header">
                    <div className="modal-title-container">
                        <h2 className="modal-title">{title}</h2>
                    </div>
                    {showCloseButton && (
                        <button
                            type="button"
                            className="modal-close-btn"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Modal Body */}
                <div className="modal-body">
                    <p className="modal-body-text pb-3">
                        If you would like to work with us or just want to get in touch, we’d love to hear from you!
                    </p>
                    {children}
                </div>
            </div>
        </div>
    );

    // Render modal using portal
    return createPortal(modalContent, document.body);
};

export default Modal;
